import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { AuthConfigResponse } from "@joogoos/contracts";
import { authClient } from "./auth.js";
import { Logo } from "./ui/logo.js";

/**
 * The app reviewer's sign-in, at `/review` ([33](../../../docs/33-the-app-reviewer.md)).
 *
 * Meta's and TikTok's reviewers need a way in, and an emailed link to an address of ours reaches nobody at Meta. So
 * one address signs in here with a password the box is configured with. It is not linked from the sign-in page: a
 * customer never sees it, and anybody else who finds it gets the same answer as a wrong password.
 */
export function ReviewLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cfg = useQuery({ queryKey: ["auth-config"], queryFn: async () => (await fetch("/v1/auth/config")).json() as Promise<AuthConfigResponse> });

  async function signIn(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const r = await authClient.signIn.email({ email: email.trim(), password, callbackURL: "/" });
      if (r.error) setError("That email and password do not sign in here.");
      // Into the app proper, at its front door rather than at `/review`.
      else location.assign("/");
    } finally {
      setBusy(false);
    }
  }

  const input = "border border-line-2 rounded-lg px-3.5 py-2.5 text-sm bg-panel outline-none focus:border-ink/40";
  return (
    <div className="h-full grid place-items-center bg-panel px-6 py-8">
      <div className="w-full max-w-[380px] flex flex-col gap-7">
        <header className="flex flex-col items-center text-center gap-1">
          <Logo size={56} />
          <h1 className="mt-1 text-2xl font-extrabold tracking-tight">joogoos</h1>
          <h2 className="text-base font-semibold text-coral">App review sign-in</h2>
          <p className="text-sm text-muted">For the platform reviewers testing Joogoos. Customers sign in at the front page.</p>
        </header>

        {cfg.data && !cfg.data.review ? (
          <p className="rounded-[10px] bg-tint px-4 py-5 text-sm text-center">
            Review access is not open at the moment. <a href="/" className="underline underline-offset-2">Sign in here</a> instead.
          </p>
        ) : (
          <form onSubmit={signIn} className="flex flex-col gap-4">
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-ink-2">Email</span>
              <input type="email" required autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} className={input} />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-ink-2">Password</span>
              <input type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className={input} />
            </label>
            <button disabled={busy} className="py-3 rounded-lg bg-coral text-white text-sm font-semibold hover:bg-coral-2 disabled:opacity-50">
              {busy ? "Signing in…" : "Sign in"}
            </button>
            {error ? <p role="alert" className="text-bad text-xs">{error}</p> : null}
          </form>
        )}

        <p className="text-xs text-faint text-center leading-relaxed">
          <a href="https://joogoos.com/terms" className="text-muted hover:text-ink">Terms</a> ·{" "}
          <a href="https://joogoos.com/privacy" className="text-muted hover:text-ink">Privacy</a>
        </p>
      </div>
    </div>
  );
}
